import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  useTheme,
  alpha,
  Card,
  CardContent,
  Divider,
  Alert,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Analytics as AnalyticsIcon,
  CompareArrows as CompareArrowsIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { HOTEL_CATEGORIES, getCategoryByName } from '../types/categories';

interface ProfitLossAnalysisProps {
  financialData: {
    years: string[];
    categories: Array<{
      id: number;
      name: string;
      type: 'revenue' | 'expense' | 'other';
      displayOrder: number;
    }>;
    benchmarks?: {
      [key: string]: {
        industry: number;
        percentile50: number;
      };
    };
  } | null;
  gridRows: Array<{
    id: string;
    categoryId: number;
    itemName: string;
    categoryName?: string;
    categoryType?: 'revenue' | 'expense' | 'other';
    [year: string]: string | number | boolean | undefined | null;
  }>;
}

interface MappedFinancialRow {
  id: string;
  categoryId: number;
  itemName: string;
  categoryName: string;
  categoryType: 'revenue' | 'expense' | 'other';
  [year: string]: string | number | boolean | undefined | null;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const formatPercent = (value: number) =>
  `${((value) * 100).toFixed(1)}%`;

const ProfitLossAnalysis: React.FC<ProfitLossAnalysisProps> = ({
  financialData,
  gridRows,
}) => {
  const theme = useTheme();

  // Process data for charts
  const processData = () => {
    console.log('Financial Data:', financialData);
    console.log('Grid Rows:', gridRows);

    if (!financialData || !gridRows || gridRows.length === 0) {
      console.log('Missing or empty financial data or grid rows');
      return null;
    }

    // Ensure we have valid years
    const years = financialData.years
      ? financialData.years.sort((a, b) => parseInt(a) - parseInt(b))
      : Array.from(new Set(gridRows.flatMap(row => Object.keys(row).filter(key => /^\d{4}$/.test(key)))))
          .sort((a, b) => parseInt(a) - parseInt(b));

    if (years.length === 0) {
      console.log('No valid years found in data');
      return null;
    }

    console.log('Sorted Years:', years);

    // Map rows to our defined categories with improved error handling
    const mappedRows: MappedFinancialRow[] = gridRows.map(row => {
      try {
        // First try to find by exact name match
        let category = getCategoryByName(row.itemName);

        // If not found, try to find by category name
        if (!category && financialData.categories) {
          const backendCategory = financialData.categories.find(c => c.id === row.categoryId);
          if (backendCategory) {
            // Try to find a matching category in our defined categories
            category = HOTEL_CATEGORIES.find(c =>
              c.name.toLowerCase() === backendCategory.name.toLowerCase() ||
              (c.name.toLowerCase().includes('revenue') && backendCategory.name.toLowerCase().includes('revenue')) ||
              (c.name.toLowerCase().includes('expense') && backendCategory.name.toLowerCase().includes('expense'))
            );
          }
        }

        // Ensure all year values are numbers
        const processedRow: MappedFinancialRow = {
          ...row,
          categoryName: category?.name || row.itemName,
          categoryType: category?.type || 'other'
        };

        years.forEach(year => {
          const value = processedRow[year];
          processedRow[year] = typeof value === 'string' ? parseFloat(value) || 0 : value || 0;
        });

        return processedRow;
      } catch (error) {
        console.error('Error processing row:', error);
        return {
          ...row,
          categoryName: row.itemName,
          categoryType: 'other'
        } as MappedFinancialRow;
      }
    });

    console.log('Mapped Rows:', mappedRows);

    // Process trend data with improved error handling
    const revenueData = years.map(year => {
      try {
        const revenue = mappedRows
          .filter(row => row.categoryType === 'revenue')
          .reduce((sum, row) => sum + (Number(row[year]) || 0), 0);

        const expenses = mappedRows
          .filter(row => row.categoryType === 'expense')
          .reduce((sum, row) => sum + (Number(row[year]) || 0), 0);

        const noi = revenue - expenses;
        const operatingMargin = revenue > 0 ? (noi / revenue) * 100 : 0;

        return {
          year,
          revenue,
          expenses,
          noi,
          operatingMargin
        };
      } catch (error) {
        console.error(`Error processing year ${year}:`, error);
        return {
          year,
          revenue: 0,
          expenses: 0,
          noi: 0,
          operatingMargin: 0
        };
      }
    });

    // Process expense breakdown for the most recent year with improved error handling
    const latestYear = years[years.length - 1];
    console.log('Latest Year:', latestYear);

    const expenseBreakdown = mappedRows
      .filter(row => row.categoryType === 'expense')
      .map(row => {
        try {
          const value = Math.abs(Number(row[latestYear]) || 0);
          return {
            name: row.categoryName || row.itemName,
            value: isNaN(value) ? 0 : value
          };
        } catch (error) {
          console.error('Error processing expense row:', error);
          return {
            name: row.itemName,
            value: 0
          };
        }
      })
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);

    console.log('Expense Breakdown:', expenseBreakdown);

    return {
      trendData: revenueData,
      expenseBreakdown,
    };
  };

  const data = processData();
  console.log('Final Processed Data:', data);

  // Calculate key statistics
  const calculateStats = () => {
    if (!data || !data.trendData.length) {
      return {
        avgRevenue: 0,
        avgExpenses: 0,
        avgNOI: 0,
        avgMargin: 0,
        revenueGrowth: 0,
        dataYears: 0
      };
    }

    const avgRevenue = data.trendData.reduce((sum, item) => sum + item.revenue, 0) / data.trendData.length;
    const avgExpenses = data.trendData.reduce((sum, item) => sum + item.expenses, 0) / data.trendData.length;
    const avgNOI = data.trendData.reduce((sum, item) => sum + item.noi, 0) / data.trendData.length;
    const avgMargin = data.trendData.reduce((sum, item) => sum + item.operatingMargin, 0) / data.trendData.length;

    // Calculate revenue growth if we have multiple years
    let revenueGrowth = 0;
    if (data.trendData.length >= 2) {
      const firstYear = data.trendData[0];
      const lastYear = data.trendData[data.trendData.length - 1];
      if (firstYear.revenue > 0) {
        revenueGrowth = ((lastYear.revenue - firstYear.revenue) / firstYear.revenue) * 100;
      }
    }

    return {
      avgRevenue,
      avgExpenses,
      avgNOI,
      avgMargin,
      revenueGrowth,
      dataYears: data.trendData.length
    };
  };

  const stats = calculateStats();

  if (!data) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            📊 Financial Analysis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Analyze revenue, expenses, and profitability trends over time.
          </Typography>
        </Alert>

        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>No financial data available for analysis</Typography>
          <Typography variant="body2" color="text.secondary">
            Add financial data to view comprehensive profit & loss analysis.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Info Section */}
      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          📊 Financial Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Comprehensive analysis of revenue, expenses, and profitability trends.
        </Typography>
      </Alert>

      {/* Quick Insights */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Financial Overview
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary">Avg Revenue</Typography>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600 }}>
                {formatCurrency(stats.avgRevenue)}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={3}>
            <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary">Avg NOI</Typography>
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                {formatCurrency(stats.avgNOI)}
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={3}>
            <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary">Avg Margin</Typography>
              <Typography variant="h6" color="info.main" sx={{ fontWeight: 600 }}>
                {stats.avgMargin.toFixed(1)}%
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={3}>
            <Card variant="outlined" sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary">Revenue Growth</Typography>
              <Typography variant="h6" color={stats.revenueGrowth >= 0 ? "success.main" : "error.main"} sx={{ fontWeight: 600 }}>
                {stats.revenueGrowth >= 0 ? '+' : ''}{stats.revenueGrowth.toFixed(1)}%
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Financial Trends Chart */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Financial Trends
        </Typography>
        <Box sx={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={formatCurrency} />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                labelFormatter={(label) => `Year: ${label}`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="expenses"
                name="Expenses"
                stroke={theme.palette.error.main}
                strokeWidth={2}
              />
              <Line
                type="monotone"
                dataKey="noi"
                name="Net Operating Income"
                stroke={theme.palette.success.main}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Paper>

      {/* Operating Margin and Analysis Grid */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Operating Margin Trend
            </Typography>
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" />
                  <YAxis tickFormatter={(value) => `${value.toFixed(1)}%`} />
                  <Tooltip
                    formatter={(value: number) => `${value.toFixed(1)}%`}
                    labelFormatter={(label) => `Year: ${label}`}
                  />
                  <Bar
                    dataKey="operatingMargin"
                    name="Operating Margin"
                    fill={theme.palette.info.main}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Expense Breakdown
            </Typography>
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.expenseBreakdown}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                  >
                    {data.expenseBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Key Insights */}
      <Paper sx={{ p: 2, mt: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          Key Insights
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Revenue Growth
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {data.trendData.length >= 2 ? (
                  `Revenue has ${data.trendData[data.trendData.length - 1].revenue > data.trendData[data.trendData.length - 2].revenue ? 'increased' : 'decreased'} by ${formatPercent(
                    (data.trendData[data.trendData.length - 1].revenue - data.trendData[data.trendData.length - 2].revenue) /
                    data.trendData[data.trendData.length - 2].revenue
                  )} year-over-year`
                ) : 'Insufficient data for comparison'}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AnalyticsIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Operating Margin
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Current operating margin is {data.trendData[data.trendData.length - 1].operatingMargin.toFixed(1)}%
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CompareArrowsIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Expense Analysis
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {data.expenseBreakdown.length > 0 ? (
                  `Largest expense: ${data.expenseBreakdown[0].name} (${formatPercent(
                    data.expenseBreakdown[0].value / data.trendData[data.trendData.length - 1].expenses
                  )})`
                ) : 'No expense data available'}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ProfitLossAnalysis;